import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { BASE_URL } from "../../../../data/BaseUrl";
import MatrixFlagCards from "./MatrixFlagCards";
import MatrixGrid from "./MatrixGrid";
import MatrixDrillDown from "./MatrixDrillDown";
import MatrixCardModal from "./MatrixCardModal";
import "../../../../styles/trainer-report/deployment-matrix.css";

const MAX_FETCH_LIMIT = 9999;
const OVERSTAFF_THRESHOLD = 6;
const MIN_STAFF = 6;

/** Build the 7-day windows for a given month (1-based month, year). */
const buildWindows = (year, month) => {
  const daysInMonth = moment(`${year}-${String(month).padStart(2, "0")}`, "YYYY-MM").daysInMonth();
  const windows = [];
  let start = 1;
  while (start <= daysInMonth) {
    const end = Math.min(start + 6, daysInMonth);
    windows.push({ start, end });
    start += 7;
  }
  return windows;
};

/** Returns an array of "YYYY-MM-DD" strings for the given window. */
const getWindowDates = (year, month, winStart, winEnd) => {
  const dates = [];
  for (let d = winStart; d <= winEnd; d++) {
    dates.push(
      moment(`${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`, "YYYY-MM-DD").format("YYYY-MM-DD")
    );
  }
  return dates;
};

const DeploymentMatrixTab = ({ gymFilter }) => {
  const now = moment();
  const [year, setYear] = useState(now.year());
  const [month, setMonth] = useState(now.month() + 1); // 1-based
  const [windowIdx, setWindowIdx] = useState(0);
  const [dayData, setDayData] = useState([]); // [{ date, records }] — one entry per window day
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [activeCell, setActiveCell] = useState(null); // { gymId, date, staffList, gymName }
  const [matrixFilter, setMatrixFilter] = useState(null); // "critical"|"overstaffed"|"best"|"absentee"|null
  const [cardModal, setCardModal] = useState(null);       // null | card key

  const token = useSelector((state) => state.auth?.user?.token);

  const windows = useMemo(() => buildWindows(year, month), [year, month]);

  // Clamp windowIdx when month changes
  useEffect(() => {
    setWindowIdx((i) => Math.min(i, windows.length - 1));
    setActiveCell(null);
    setMatrixFilter(null);
  }, [year, month, windows.length]);

  // Clamp to current week when first rendered
  useEffect(() => {
    const todayMoment = moment();
    if (todayMoment.year() === year && todayMoment.month() + 1 === month) {
      const dayOfMonth = todayMoment.date();
      const idx = windows.findIndex((w) => w.start <= dayOfMonth && w.end >= dayOfMonth);
      if (idx >= 0) setWindowIdx(idx);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run only once on mount

  const currentWindow = windows[windowIdx] || windows[0];
  const windowDates = useMemo(
    () => currentWindow ? getWindowDates(year, month, currentWindow.start, currentWindow.end) : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [year, month, currentWindow?.start, currentWindow?.end]
  );

  // Date range for API call
  const dateFrom = windowDates[0] || "";
  const dateTo   = windowDates[windowDates.length - 1] || "";

  // Fetch data: one parallel call per day in the window.
  // The backend processStaffDetails collapses all attendance into ONE record per shift,
  // using the first check-in date. Multi-day calls produce wrong dates for the matrix.
  // Single-day calls (date_from === date_to) are always accurate.
  useEffect(() => {
    if (!token || windowDates.length === 0) return;
    const controller = new AbortController();

    const fetchDay = async (date) => {
      const params = new URLSearchParams({
        date_from: date,
        date_to:   date,
        limit:     MAX_FETCH_LIMIT,
        page:      1,
      });
      if (gymFilter?.value) params.set("gym_id", gymFilter.value);
      const res = await fetch(`${BASE_URL}/staff/report/v3?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const json = await res.json();
      const records = Array.isArray(json.data)    ? json.data
                    : Array.isArray(json.result)  ? json.result
                    : Array.isArray(json.records) ? json.records
                    : [];
      return { date, records };
    };

    const fetchAll = async () => {
      setDayData([]);
      setLoading(true);
      setError(null);
      try {
        const results = await Promise.all(windowDates.map(fetchDay));
        setDayData(results);
      } catch (err) {
        if (err.name === "AbortError") return;
        setError("Failed to load data. Please retry.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchAll();
    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, token, reloadTrigger, gymFilter]);

  // Reset cell selection when gym filter changes
  useEffect(() => {
    setActiveCell(null);
  }, [gymFilter]);

  /**
   * Group dayData into gymDateMap:
   *   { [gymId]: { gym_name, dates: { [date]: { trainers, receptionists } } } }
   *
   * Each entry in dayData is { date, records } from a single-day API call.
   * We use the call date directly — not attendance_details.date — so absent staff
   * (who have date: null in the backend response) are correctly placed in their day.
   */
  const gymDateMap = useMemo(() => {
    return dayData.reduce((acc, { date, records }) => {
      records.forEach((staff) => {
        const { gym_id, gym_name, account_type } = staff;
        if (!gym_id) return;
        if (!acc[gym_id]) acc[gym_id] = { gym_name, dates: {} };
        if (!acc[gym_id].dates[date]) {
          acc[gym_id].dates[date] = { trainers: [], receptionists: [] };
        }

        (staff.shift_details || []).forEach((shift) => {
          const att = shift.attendance_details;
          const isPresent =
            att?.checkin_status === "Checked-In" ||
            att?.checkout_status === "checked-out";
          const isOnLeave = att?.status_value === "on_leave";
          const isDayOff  = att?.status_value === "day_off";
          const isExpected = !isOnLeave && !isDayOff;
          const { shift_details: _sd, ...staffWithoutShifts } = staff;
          const entry = { ...staffWithoutShifts, shift_details: [shift], isPresent, isExpected, isOnLeave, isDayOff, shift };

          if (account_type === "trainer")      acc[gym_id].dates[date].trainers.push(entry);
          if (account_type === "receptionist") acc[gym_id].dates[date].receptionists.push(entry);
        });
      });
      return acc;
    }, {});
  }, [dayData]);

  /** Compute flag card analytics from the full window. */
  const analytics = useMemo(() => {
    const todayStr = moment().format("YYYY-MM-DD");
    let criticalCount    = 0;
    let overstaffedCount = 0;
    let bestGymName      = "";
    let bestGreenCount   = -1;
    let absenteeCount    = 0;
    let totalPresent     = 0;
    let totalSlots       = 0;

    Object.entries(gymDateMap).forEach(([, { gym_name, dates }]) => {
      let isCritical    = false;
      let isOverstaffed = false;
      let greenCount    = 0;

      // Absentee: staff with >=3 absent records across the window
      const absenteeMap = {};

      windowDates.forEach((date) => {
        const day = dates[date];
        if (!day) return;
        const all = [...(day.trainers || []), ...(day.receptionists || [])];
        const present = all.filter((s) => s.isPresent).length;

        if (date <= todayStr) {
          totalPresent += present;
          totalSlots++;
          if (present === 0) isCritical = true;
          if (present > OVERSTAFF_THRESHOLD) isOverstaffed = true;
          if (present >= MIN_STAFF) greenCount++;

          // Count absentees
          all.forEach((s) => {
            const att = s.shift?.attendance_details;
            if (att?.status_value === "absent" || (!att?.checkin_status && att?.status_value !== "on_leave" && att?.status_value !== "day_off")) {
              absenteeMap[s.user_id] = (absenteeMap[s.user_id] || 0) + 1;
            }
          });
        }
      });

      if (isCritical) criticalCount++;
      if (isOverstaffed) overstaffedCount++;
      if (greenCount > bestGreenCount) {
        bestGreenCount = greenCount;
        bestGymName    = gym_name;
      }
      if (Object.values(absenteeMap).some((n) => n >= 3)) absenteeCount++;
    });

    const networkAvg = totalSlots > 0
      ? (totalPresent / totalSlots).toFixed(1)
      : "—";

    return {
      criticalCount,
      overstaffedCount,
      bestGymName,
      bestGymStreak: bestGreenCount,
      absenteeCount,
      networkAvg,
      networkGymCount: Object.keys(gymDateMap).length,
    };
  }, [gymDateMap, windowDates]);

  /** Apply flag filter to gymDateMap entries. */
  const filteredGymDateMap = useMemo(() => {
    if (!matrixFilter || matrixFilter === "avg") return gymDateMap;
    const todayStr = moment().format("YYYY-MM-DD");

    return Object.fromEntries(
      Object.entries(gymDateMap).filter(([, { dates }]) => {
        const cells = windowDates
          .filter((d) => d <= todayStr)
          .map((d) => {
            const day = dates[d];
            if (!day) return 0;
            return [...(day.trainers || []), ...(day.receptionists || [])].filter((s) => s.isPresent).length;
          });

        if (matrixFilter === "critical")    return cells.some((p) => p === 0);
        if (matrixFilter === "overstaffed") return cells.some((p) => p > OVERSTAFF_THRESHOLD);
        if (matrixFilter === "best")        return cells.every((p) => p >= MIN_STAFF);
        if (matrixFilter === "absentee") {
          // Match analytics: gym has a staff member with >=3 absent/not-checked-in records
          const absenteeMap = {};
          windowDates
            .filter((d) => d <= todayStr)
            .forEach((d) => {
              const day = dates[d];
              if (!day) return;
              [...(day.trainers || []), ...(day.receptionists || [])].forEach((s) => {
                const att = s.shift?.attendance_details;
                if (
                  att?.status_value === "absent" ||
                  (!att?.checkin_status && att?.status_value !== "on_leave" && att?.status_value !== "day_off")
                ) {
                  absenteeMap[s.user_id] = (absenteeMap[s.user_id] || 0) + 1;
                }
              });
            });
          return Object.values(absenteeMap).some((n) => n >= 3);
        }
        return true;
      })
    );
  }, [gymDateMap, matrixFilter, windowDates]);

  const handleFilterToggle = useCallback((key) => {
    setMatrixFilter((prev) => (prev === key ? null : key));
    setActiveCell(null);
  }, []);

  const handleCardClick = useCallback((key) => {
    setCardModal(key);
  }, []);

  const handleCellClick = useCallback((gymId, date, staffList, gymName) => {
    setActiveCell((prev) =>
      prev?.gymId === gymId && prev?.date === date
        ? null
        : { gymId, date, staffList, gymName }
    );
  }, []);

  const goToPrevMonth = () => {
    const m = moment(`${year}-${month}`, "YYYY-M").subtract(1, "month");
    setYear(m.year());
    setMonth(m.month() + 1);
    setWindowIdx(0);
  };
  const goToNextMonth = () => {
    const m = moment(`${year}-${month}`, "YYYY-M").add(1, "month");
    const isAfterToday = m.isAfter(moment(), "month");
    if (!isAfterToday) {
      setYear(m.year());
      setMonth(m.month() + 1);
      setWindowIdx(0);
    }
  };

  const isNextMonthDisabled = moment(`${year}-${month}`, "YYYY-M").isSameOrAfter(moment(), "month");
  const monthLabel = moment(`${year}-${String(month).padStart(2, "0")}`, "YYYY-MM").format("MMMM YYYY");

  return (
    <div className="sdt-container">
      {/* Analytics flag cards */}
      <MatrixFlagCards
        analytics={analytics}
        activeFilter={matrixFilter}
        onFilter={handleFilterToggle}
        onCardClick={handleCardClick}
      />

      {/* Week navigator */}
      <div className="sdm-week-nav">
        <button
          type="button"
          className="sdm-week-nav__arrow"
          onClick={goToPrevMonth}
          aria-label="Previous month"
        >
          &#8592;
        </button>
        <span className="sdm-week-nav__month">{monthLabel}</span>
        <div className="sdm-week-nav__windows">
          {windows.map((w, i) => (
            <button
              key={i}
              type="button"
              className={`sdm-week-nav__btn${windowIdx === i ? " sdm-week-nav__btn--active" : ""}`}
              onClick={() => { setWindowIdx(i); setActiveCell(null); }}
            >
              {w.start}–{w.end}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="sdm-week-nav__arrow"
          onClick={goToNextMonth}
          disabled={isNextMonthDisabled}
          aria-label="Next month"
        >
          &#8594;
        </button>
        <button
          type="button"
          className="sdt-date-nav__reload"
          onClick={() => setReloadTrigger((n) => n + 1)}
          aria-label="Reload"
          style={{ marginLeft: "auto" }}
        >
          ↻ Reload
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div className="sdm-error">
          {error}{" "}
          <button type="button" className="sdm-error__retry" onClick={() => setReloadTrigger((n) => n + 1)}>
            Retry
          </button>
        </div>
      )}

      {/* Heatmap */}
      {!error && (
        <MatrixGrid
          gymDateMap={filteredGymDateMap}
          windowDates={windowDates}
          activeCell={activeCell}
          onCellClick={handleCellClick}
          loading={loading}
        />
      )}

      {/* Empty state */}
      {!loading && !error && Object.keys(filteredGymDateMap).length === 0 && (
        <div className="sdm-empty">No gym data found for this window.</div>
      )}

      {/* Drill-down center modal */}
      {activeCell && (
        <MatrixDrillDown
          gymName={activeCell.gymName}
          date={activeCell.date}
          staffList={activeCell.staffList}
          onClose={() => setActiveCell(null)}
        />
      )}

      {/* Card detail center modal */}
      {cardModal && (
        <MatrixCardModal
          modalKey={cardModal}
          gymDateMap={gymDateMap}
          windowDates={windowDates}
          onClose={() => setCardModal(null)}
        />
      )}
    </div>
  );
};

export default DeploymentMatrixTab;
